import { assertNever } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { HYDRATED_UPDATED_AT } from '#pplx/frontend/react-query/internal/consts.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { hashKey } from '@tanstack/react-query';
/**
 * Delay before a changed query is written, which also caps writes per key to one per interval.
 * Every IndexedDB write structured-clones its value on the main thread, and a streaming answer
 * replaces its thread queries on every message, so writes keep only the latest data per key.
 */
export const QUERY_CACHE_PERSIST_INTERVAL_MS = 1_000;
function isInfiniteQueryData(data) {
    return (typeof data === 'object' &&
        data !== null &&
        'pages' in data &&
        Array.isArray(data.pages) &&
        'pageParams' in data &&
        Array.isArray(data.pageParams));
}
function getPersistableData(query) {
    const data = query.state.data;
    // Infinite queries persist only their first page to keep IndexedDB lean.
    return isInfiniteQueryData(data)
        ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) }
        : data;
}
function changesData(action) {
    switch (action.type) {
        case 'success':
            // Hydrated data came from this cache, so writing it back would copy it unchanged.
            return action.dataUpdatedAt !== HYDRATED_UPDATED_AT;
        case 'setState':
            return 'data' in action.state;
        case 'fetch':
        case 'failed':
        case 'error':
        case 'invalidate':
        case 'pause':
        case 'continue':
            return false;
        default:
            assertNever(action);
            return false;
    }
}
function getPersistedChange(event) {
    switch (event.type) {
        case 'added':
            return 'write';
        case 'updated':
            return changesData(event.action) ? 'write' : undefined;
        case 'removed':
            return 'remove';
        case 'observerAdded':
        case 'observerRemoved':
        case 'observerResultsUpdated':
        case 'observerOptionsUpdated':
            return undefined;
        default:
            assertNever(event);
            return undefined;
    }
}
/**
 * Mirrors versioned queries into IndexedDB, coalescing each key's writes to its latest data.
 * Pending writes flush once the page is hidden: mobile browsers can discard a hidden page without
 * firing `pagehide`, so that is the last point where a transaction reliably starts.
 */
export function persistQueryCache(queryCache, target, intervalMs = QUERY_CACHE_PERSIST_INTERVAL_MS) {
    const pendingWrites = new Map();
    const cancel = (persistKey) => {
        const pending = pendingWrites.get(persistKey);
        if (!pending)
            return undefined;
        clearTimeout(pending.timer);
        pendingWrites.delete(persistKey);
        return pending;
    };
    const flush = (persistKey) => {
        const pending = cancel(persistKey);
        // A write starts only while its store is still active. Activation copies the default store
        // into the account's store and clears it, and sign-out clears only the active account's
        // store, so a write queued before either could restore data that no cleanup will remove.
        if (pending && pending.store === target.getStore()) {
            target.write(persistKey, pending.data, pending.store);
        }
    };
    const flushAll = () => {
        [...pendingWrites.keys()].forEach(flush);
    };
    const discardPendingWrites = () => {
        [...pendingWrites.keys()].forEach(cancel);
    };
    const schedule = (persistKey, data) => {
        const store = target.getStore();
        const pending = pendingWrites.get(persistKey);
        if (pending && pending.store === store) {
            pending.data = data;
            return;
        }
        // A different store means the active account changed, so the earlier write is dropped.
        cancel(persistKey);
        pendingWrites.set(persistKey, {
            data,
            store,
            timer: setTimeout(() => flush(persistKey), intervalMs),
        });
    };
    const unsubscribe = queryCache.subscribe((event) => {
        const change = getPersistedChange(event);
        if (!change)
            return;
        const { query } = event;
        if (!QueryClientHelper.isVersionedQueryKey(query.queryKey))
            return;
        // Queries opting out of IDB hydration (e.g. user-specific connection state that would render
        // stale-then-fresh on every page load) skip both write and read.
        if (query.meta?.skipPersistence === true)
            return;
        const persistKey = hashKey(query.queryKey);
        if (change === 'remove') {
            cancel(persistKey);
            target.remove(persistKey, target.getStore());
            return;
        }
        const data = getPersistableData(query);
        if (data === undefined || data === null)
            return;
        schedule(persistKey, data);
    });
    const flushWhenHidden = () => {
        if (document.visibilityState === 'hidden')
            flushAll();
    };
    document.addEventListener('visibilitychange', flushWhenHidden);
    window.addEventListener('pagehide', flushAll);
    return {
        discardPendingWrites,
        dispose: () => {
            unsubscribe();
            document.removeEventListener('visibilitychange', flushWhenHidden);
            window.removeEventListener('pagehide', flushAll);
            discardPendingWrites();
        },
    };
}
//# sourceMappingURL=queryCachePersistence.js.map